import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  limitReviews,
  pipes,
  type BrokerDispatch,
  type LimitReviewRequest,
  type Policyholder,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime, formatMoney } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Modal } from '@/shared/ui/Modal';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { LimitReviewRequestDialog } from '../../pipes/PipeOpenCardDialog';
import { gruposDeModalidade } from '../rotulos';

/**
 * Secao Revisao de Limite do tomador (P-EST-2): os pedidos de revisao de limite, o
 * botao de pedir e o cancelamento do pedido pendente. O pedido abre o cartao Revisao de
 * Limite na esteira Credito; o limite novo aprovado na conclusao aparece em Limites e
 * Taxas. A corretora pede para os tomadores dela. Ver
 * docs/ADR/0056-esteiras-sujeitos-e-envio.md e docs/ADR/0102-consulta-no-cadastro-do-tomador.md.
 */
export function RevisaoDeLimite({ tomador }: { tomador: Policyholder }) {
  const { can, session } = useAuth();
  // A corretora cancela so os pedidos que ela abriu; o da seguradora e encerrado pela
  // seguradora (o servidor responde 409).
  const daCorretora = session?.broker_scoped === true;
  const podeCancelar = (p: LimitReviewRequest) =>
    p.status === 'pendente' &&
    can('credito.revisao_limite.create') &&
    (!daCorretora || p.requested_by_broker);
  const toast = useToast();
  const queryClient = useQueryClient();
  const [pedindo, setPedindo] = useState(false);
  // O cancelamento encerra o cartao do pedido: pede confirmacao, com a observacao
  // opcional de quem cancela (seja a corretora, seja a seguradora).
  const [cancelando, setCancelando] = useState<LimitReviewRequest | null>(null);
  const [nota, setNota] = useState('');

  const consulta = useQuery({
    queryKey: ['limit-reviews', tomador.id],
    queryFn: ({ signal }) =>
      limitReviews.list({ policyholder_id: tomador.id, limit: 50 }, signal),
  });

  const fecharCancelamento = () => {
    setCancelando(null);
    setNota('');
  };

  const cancelar = useMutation({
    mutationFn: (pedido: LimitReviewRequest) =>
      limitReviews.cancel(pedido.id, nota.trim()),
    onSuccess: () => {
      toast.success('Pedido cancelado.');
      void queryClient.invalidateQueries({ queryKey: ['limit-reviews'] });
      // O cartao do pedido vai para Cancelado na esteira Credito.
      void queryClient.invalidateQueries({ queryKey: ['pipes'] });
      void queryClient.invalidateQueries({ queryKey: ['pipe-cards'] });
      fecharCancelamento();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const meta = (consulta.data?.meta ?? {}) as {
    can_request?: boolean;
    pending_groups?: string[];
  };
  const podePedir = meta.can_request === true && can('credito.revisao_limite.create');

  return (
    <Card>
      <CardHeader
        title="Revisao de Limite"
        description="Pedidos de revisao do limite do tomador. O pedido abre o cartao na esteira Credito, e o limite novo aprovado na conclusao aparece em Limites e Taxas."
        action={
          podePedir ? (
            <Button size="sm" onClick={() => setPedindo(true)}>
              Pedir revisao de limite
            </Button>
          ) : null
        }
      />
      <CardBody>
        {consulta.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar os pedidos">
            {consulta.error.message}
          </Alert>
        ) : (
          <DataTable
            columns={[
              {
                key: 'data',
                header: 'Pedido em',
                render: (p: LimitReviewRequest) => formatDateTime(p.requested_at),
              },
              {
                key: 'grupo',
                header: 'Grupo',
                render: (p) => gruposDeModalidade[p.modality_group] ?? p.modality_group,
              },
              {
                key: 'valor',
                header: 'Limite pretendido',
                align: 'right',
                render: (p) => formatMoney(p.requested_amount),
              },
              {
                key: 'situacao',
                header: 'Situacao',
                render: (p) => <StatusBadge kind="pedidoCartao" value={p.status} />,
              },
              {
                key: 'quem',
                header: 'Solicitado por',
                hideBelow: 'md',
                render: (p) => p.requested_label || '-',
              },
              {
                key: 'acoes',
                header: '',
                render: (p) =>
                  podeCancelar(p) ? (
                    <Button size="sm" variant="ghost" onClick={() => setCancelando(p)}>
                      Cancelar pedido
                    </Button>
                  ) : null,
              },
            ]}
            items={consulta.data?.items ?? []}
            keyOf={(p) => p.id}
            loading={consulta.isPending}
            empty={
              <EmptyState
                icon="coins"
                title="Sem pedidos"
                description="Nenhum pedido de revisao de limite para este tomador."
              />
            }
          />
        )}
      </CardBody>
      <LimitReviewRequestDialog
        open={pedindo}
        onClose={() => setPedindo(false)}
        policyholder={{ id: tomador.id, label: tomador.legal_name }}
        pendingGroups={meta.pending_groups ?? []}
      />
      <Modal
        open={cancelando !== null}
        onClose={fecharCancelamento}
        title="Cancelar o pedido de revisao de limite"
        description={
          cancelando
            ? `${gruposDeModalidade[cancelando.modality_group] ?? cancelando.modality_group} - ${formatMoney(cancelando.requested_amount)}`
            : undefined
        }
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={fecharCancelamento}
              disabled={cancelar.isPending}
            >
              Voltar
            </Button>
            <Button
              variant="danger"
              loading={cancelar.isPending}
              onClick={() => {
                if (cancelando) cancelar.mutate(cancelando);
              }}
            >
              Cancelar pedido
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-ink-600">
            O pedido sai da analise e o cartao vai para Cancelado na esteira Credito. O
            limite nao muda.
          </p>
          <Textarea
            label="Observacao"
            value={nota}
            maxLength={2000}
            hint="Opcional. Fica no pedido e no cartao, com quem cancelou e quando."
            onChange={(event) => setNota(event.target.value)}
          />
        </div>
      </Modal>
    </Card>
  );
}

/**
 * Secao Envios da seguradora (P-EST-2): o que a seguradora enviou ao corretor pelas
 * esteiras sobre este tomador e as cotacoes dele, com quem enviou e quando. A corretora
 * ve so o que foi enviado a ela.
 */
export function EnviosDaSeguradora({ tomador }: { tomador: Policyholder }) {
  const consulta = useQuery({
    queryKey: ['broker-dispatches', tomador.id],
    queryFn: ({ signal }) =>
      pipes.brokerDispatches({ policyholder_id: tomador.id, limit: 50 }, signal),
  });
  return (
    <Card>
      <CardHeader
        title="Envios da seguradora"
        description="Mensagens e pedidos de documentos enviados ao corretor pelas esteiras. O aviso por e-mail ainda depende de regra da seguradora."
      />
      <CardBody>
        {consulta.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar os envios">
            {consulta.error.message}
          </Alert>
        ) : (
          <DataTable
            columns={[
              {
                key: 'data',
                header: 'Enviado em',
                render: (e: BrokerDispatch) => formatDateTime(e.sent_at),
              },
              {
                key: 'tipo',
                header: 'Tipo',
                render: (e) => (
                  <Badge tone="brand">
                    {e.kind === 'documentos' ? 'Documentos solicitados' : 'Follow up'}
                  </Badge>
                ),
              },
              { key: 'esteira', header: 'Esteira', render: (e) => e.pipe.name },
              {
                key: 'mensagem',
                header: 'Mensagem',
                render: (e) => (
                  <span className="line-clamp-2 whitespace-pre-line">{e.message}</span>
                ),
              },
              {
                key: 'quem',
                header: 'Enviado por',
                hideBelow: 'md',
                render: (e) => e.sent_label || '-',
              },
            ]}
            items={consulta.data?.items ?? []}
            keyOf={(e) => e.id}
            loading={consulta.isPending}
            empty={
              <EmptyState
                icon="send"
                title="Sem envios"
                description="A seguradora ainda nao enviou nada ao corretor sobre este tomador."
              />
            }
          />
        )}
      </CardBody>
    </Card>
  );
}
